import { handleUpdateOrderApi } from "@/utils/apis/pharmacy";

import {
  IOrderStatusEnum,
  IPharmacyDiagnosticOrderFilterEnum,
  IPharmacyOrderDetailUpdate,
} from "@/models/ApiRequestResponse/pharmacy";
import { IPharmacyDiagnosticOrderDetail } from "@/models/ApiRequestResponse/diagnostic";
import { IRoleType } from "@/models/role";
import { ISessionKeys } from "@/models";

import {
  fetchPharmacySearchOrdersList,
  fetchSearchDiagnosticOrdersList,
  updatePendingOrderList,
} from "@/store/slices/pharmacyClinicDiagnostic";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { INotifyEnum, notify } from "@/utils/toaster";

import { storage } from "@/utils/Storage";

import { updateDiagnosticOrderApi } from "../apis/diagnostic";

type ResetPaginationCallback = () => void;

const useHandlePharmacyOrderUpdate = (
  resetPaginationCallback?: ResetPaginationCallback
) => {
  const dispatch = useAppDispatch();

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const isDiagnosticCenter = roleType === IRoleType.DIAGNOSTIC_CENTER;

  const { pendingOrdersList, order, searchKeyword } = useAppSelector(
    (state) => state.pharmacyClinicDiagnostic
  );

  const handlePharmacyOrderUpdate = async (
    status: IOrderStatusEnum,
    values?: Partial<IPharmacyOrderDetailUpdate> | null,
    medicalRecordIds?: string[]
  ) => {
    try {
      let response;
      if (isDiagnosticCenter) {
        response = await updateDiagnosticOrderApi({
          ...(status ? { status } : {}),
          id: order?.id,
          ...(values?.discount && { discountAmount: Number(values?.discount) }),
          ...(medicalRecordIds ? { medicalRecordIds } : {}),
        });
      } else {
        response = await handleUpdateOrderApi({
          ...(values &&
          order.type === IPharmacyDiagnosticOrderFilterEnum.HOME_DELIVERY
            ? {
                amount: values?.totalPayable,
                tracking_id: values?.trackingId,
                delivery_partner: values?.deliveryPartner,
              }
            : {
                amount: values?.totalPayable,
              }),
          orderId: order?.id,
          status,
        });
      }

      if (response.statusCode === 200) {
        const { id } = order as IPharmacyDiagnosticOrderDetail;
        if (searchKeyword) {
          switch (roleType) {
            case IRoleType.PHARMACY:
              dispatch(
                fetchPharmacySearchOrdersList({
                  keyword: searchKeyword,
                  offset: 0,
                })
              );
              break;
            case IRoleType.DIAGNOSTIC_CENTER:
              dispatch(
                fetchSearchDiagnosticOrdersList({ keyword: searchKeyword })
              );
              break;
            default:
              break;
          }
        }

        if (
          status === IOrderStatusEnum.COMPLETED ||
          status === IOrderStatusEnum.UPLOADED ||
          status === IOrderStatusEnum.CANCELLED
        ) {
          resetPaginationCallback?.();
          notify(INotifyEnum.SUCCESS, response.message);
          return;
        } else {
          const newPendingOrderList = pendingOrdersList.filter(
            (order) => order?.id !== id
          );
          dispatch(updatePendingOrderList([...newPendingOrderList]));

          resetPaginationCallback?.();
        }
        notify(INotifyEnum.SUCCESS, response.message);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return { handlePharmacyOrderUpdate };
};

export default useHandlePharmacyOrderUpdate;
